<template>
    <div class="fee-structure-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة هيكلة الرسوم (التسعير)</h2>
          </div>
        </template>
  
        <el-alert
          title="ما هي هذه الواجهة؟"
          type="info"
          description="هذه الواجهة تسمح لك بتحديد 'تسعيرة' كل بند من بنود الرسوم. اختر أولاً السنة الدراسية، ثم يمكنك تحديد أسعار عامة لهذه السنة، أو تخصيص أسعار مختلفة لكل مستوى دراسي على حدة."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-row :gutter="20">
          <el-col :span="12">
              <el-form-item label="1. اختر السنة الدراسية">
                  <el-select v-model="filters.study_year_id" placeholder="اختر السنة الدراسية" @change="handleFilterChange" style="width: 100%;" clearable>
                      <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
                  </el-select>
              </el-form-item>
          </el-col>
          <el-col :span="12">
              <el-form-item label="2. اختر المستوى الدراسي (اختياري)">
                  <el-select v-model="filters.grade_level_id" placeholder="عام لجميع المستويات" @change="handleFilterChange" style="width: 100%;" clearable :disabled="!filters.study_year_id">
                      <el-option v-for="level in gradeLevels" :key="level.id" :label="level.name" :value="level.id" />
                  </el-select>
              </el-form-item>
          </el-col>
        </el-row>
        
        <el-divider v-if="filters.study_year_id" />
  
        <div v-if="filters.study_year_id">
          <el-button type="primary" @click="handleCreate" style="margin-bottom: 20px;">
              <el-icon><Plus /></el-icon> إضافة تسعيرة جديدة لهذا النطاق
          </el-button>
          
          <el-table :data="feeStructures" v-loading="loading" border style="width: 100%">
            <el-table-column prop="fee_type.name" label="اسم البند" />
            <el-table-column prop="amount" label="المبلغ">
              <template #default="{ row }">
                {{ row.amount }} دينار
              </template>
            </el-table-column>
            <el-table-column label="الإجراءات" width="150" fixed="right">
              <template #default="{ row }">
                <el-button size="small" @click="handleEdit(row)">تعديل المبلغ</el-button>
                <el-popconfirm title="هل أنت متأكد من حذف هذا السعر؟" @confirm="handleDelete(row.id)">
                  <template #reference><el-button size="small" type="danger">حذف</el-button></template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else>
          <el-empty description="الرجاء اختيار السنة الدراسية لعرض الأسعار." />
        </div>
  
      </el-card>
  
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="بند الرسوم" prop="fee_type_id">
              <el-select v-model="form.fee_type_id" placeholder="اختر بند الرسوم" style="width:100%" :disabled="!!form.id">
                  <el-option v-for="item in feeTypes" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
          </el-form-item>
          <el-form-item label="المبلغ (دينار)" prop="amount">
            <el-input-number v-model="form.amount" :min="0" :precision="2" :step="10" style="width: 100%;" />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="dialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">
              {{ form.id ? 'تحديث' : 'حفظ' }}
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus } from '@element-plus/icons-vue';
  
  export default {
    name: 'FeeStructureManagement',
    components: { Plus, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: false,
        formLoading: false,
        dialogVisible: false,
        
        filters: {
          study_year_id: null,
          grade_level_id: null,
        },
        
        studyYears: [],
        gradeLevels: [],
        feeTypes: [],
        feeStructures: [],
        
        // --- التعديل الرئيسي هنا: تهيئة الفورم بشكل مباشر ---
        form: { 
          id: null,
          study_year_id: null,
          grade_level_id: null,
          fee_type_id: null, 
          amount: 0 
        },
        rules: {
          fee_type_id: [{ required: true, message: 'بند الرسوم مطلوب', trigger: 'change' }],
          amount: [{ required: true, message: 'المبلغ مطلوب', trigger: 'blur' }],
        },
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل سعر البند' : 'إضافة تسعيرة جديدة';
      },
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchInitialData();
    },
    methods: {
      getInitialForm() {
        return { 
          id: null,
          study_year_id: this.filters.study_year_id,
          grade_level_id: this.filters.grade_level_id,
          fee_type_id: null, 
          amount: 0 
        };
      },
      async fetchInitialData() {
          try {
              const [studyYearsRes, gradeLevelsRes, feeTypesRes] = await Promise.all([
                  this.api.get('/study-years'),
                  this.api.get('/grade-levels'),
                  this.api.get('/fee-types'),
              ]);
              this.studyYears = studyYearsRes.data.data || studyYearsRes.data;
              this.gradeLevels = gradeLevelsRes.data;
              this.feeTypes = feeTypesRes.data;
          } catch (error) {
              ElMessage.error('Failed to load initial data.');
          }
      },
      handleFilterChange() {
        if (this.filters.study_year_id) {
          this.fetchFeeStructures();
        } else {
          this.feeStructures = [];
        }
      },
      async fetchFeeStructures() {
        this.loading = true;
        try {
          const response = await this.api.get('/fee-structures', { params: this.filters });
          this.feeStructures = response.data;
        } catch (error) {
          ElMessage.error('Failed to fetch fee structures.');
        } finally {
          this.loading = false;
        }
      },
      handleCreate() {
        this.form = this.getInitialForm();
        this.dialogVisible = true;
      },
      handleEdit(structure) {
        this.form = { ...structure };
        this.dialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            if (this.form.id) {
              await this.api.put(`/fee-structures/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث السعر بنجاح!');
            } else {
              await this.api.post('/fee-structures', this.form);
              ElMessage.success('تم إضافة السعر بنجاح!');
            }
            this.dialogVisible = false;
            this.fetchFeeStructures();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
        try {
          await this.api.delete(`/fee-structures/${id}`);
          ElMessage.success('تم حذف السعر بنجاح!');
          this.fetchFeeStructures();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشل في حذف السعر.');
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .fee-structure-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  </style>
  